"use client";

import { useEffect, useState } from "react";

export interface SectionNavItem {
  id: string;
  label: string;
}

// Sommaire flottant : ancres vers les sections réellement présentes sur cette
// page, la section visible à l'écran est mise en avant (IntersectionObserver).
// Masqué en dessous de 2xl : pas de place pour une colonne latérale sans
// chevaucher le contenu sur un écran de laptop classique.
export function SectionNav({ items, ariaLabel = "Sommaire de la page" }: { items: SectionNavItem[]; ariaLabel?: string }) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");

  useEffect(() => {
    const elements = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  return (
    <nav
      aria-label={ariaLabel}
      className="fixed left-6 top-1/2 z-40 hidden w-max max-w-[150px] -translate-y-1/2 xl:block"
    >
      {/* Fond opaque propre : sans lui, le texte sombre devient illisible dès
          qu'une bande sombre (how-it-works, navy) passe derrière le sommaire. */}
      <ul className="space-y-0.5 rounded-xl border border-line bg-paper p-2 shadow-sm">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={active ? "location" : undefined}
                className={`flex items-center gap-1 text-[11px] leading-tight transition-colors ${
                  active ? "font-medium text-ink" : "text-ink/40 hover:text-ink/70"
                }`}
              >
                <span aria-hidden>—</span>
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
